// ImportModal.jsx (phosphor-react + sonner toast + revised components feel)
import React, { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { X, DownloadSimple, FileXls } from "phosphor-react"

function ImportModal({ isOpen, onClose, categories, setDone, done }) {
  const [formData, setFormData] = useState({ file: null, category_id: "" })
  const [categoryType, setCategoryType] = useState("main")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setFormData({ file: null, category_id: "" })
      setCategoryType("main")
      setLoading(false)
    }
  }, [isOpen])

  const handleChange = (e) => {
    const { name, type, files, value } = e.target

    if (type === "file") {
      const file = files?.[0]
      if (!file) return

      const validExtensions = [".xls", ".xlsx"]
      const validMimeTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ]

      const okExt = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
      const okMime = validMimeTypes.includes(file.type)

      if (!okExt && !okMime) {
        toast.error("Please upload a valid Excel file (.xls or .xlsx).")
        return
      }

      setFormData((s) => ({ ...s, [name]: file }))
      return
    }

    setFormData((s) => ({ ...s, [name]: value }))
  }

  const saveImport = async (e) => {
    e.preventDefault()

    if (!formData.file) {
      toast.warning("Please select a file.")
      return
    }

    if (categoryType === "sub" && !formData.category_id) {
      toast.warning("Select a parent category.")
      return
    }

    try {
      setLoading(true)
      const fd = new FormData()
      fd.append("file", formData.file)
      if (categoryType === "sub") fd.append("category_id", formData.category_id)

      const url =
        categoryType === "sub" ? "/subcategories/importCategories" : "/categories/importCategories"

      const res = await axiosInstance.post(url, fd)
      toast.success(res?.data?.message || "Imported successfully")
      onClose?.()
      setDone(!done)
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error occurred during file upload.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50"
        >
          <div className="absolute inset-0 bg-black/70" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative mx-auto mt-20 w-[92%] max-w-lg rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xl"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
              <div className="text-start">
                <div className="text-sm font-semibold text-neutral-900 dark:text-white">Import Categories</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Upload Excel file to import categories.
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="h-9 w-9 inline-flex items-center justify-center rounded-xl border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={saveImport} className="p-5 space-y-4">
              <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 p-4">
                <div className="text-start text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
                  Category Type
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                    <input
                      type="radio"
                      value="main"
                      checked={categoryType === "main"}
                      onChange={() => setCategoryType("main")}
                      className="h-4 w-4"
                    />
                    Main Category
                  </label>

                  <label className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                    <input
                      type="radio"
                      value="sub"
                      checked={categoryType === "sub"}
                      onChange={() => setCategoryType("sub")}
                      className="h-4 w-4"
                    />
                    Subcategory
                  </label>
                </div>
              </div>

              {categoryType === "sub" && (
                <div className="text-start">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-2">
                    Parent Category
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    className="w-full h-11 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950/40 px-3 text-sm text-neutral-800 dark:text-neutral-100"
                    required
                  >
                    <option value="">Select a parent category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="text-start">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-2">
                  Excel File
                </label>

                <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 p-4 flex items-center gap-3">
                  <FileXls size={20} className="text-neutral-500" />
                  <input
                    type="file"
                    name="file"
                    onChange={handleChange}
                    className="block w-full text-sm"
                    accept=".xls,.xlsx"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-xl hover:bg-cyan-400 disabled:opacity-60"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="animate-spin inline-block size-4 border-[2px] border-current border-t-transparent rounded-full" />
                      Importing...
                    </span>
                  ) : (
                    <>
                      <DownloadSimple size={18} />
                      Import
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ImportModal
